import { Flex, Text, IconButton, Icon, Image, Skeleton, icon as iconSize } from '@nula/core-ui';
import clsx from 'clsx';
import { useOgPreview } from '../hooks/use-og-preview';
import styles from './link-unfurl-card.module.css';

export interface LinkUnfurlCardProps {
  /** The external URL to preview. */
  href: string;
  /** Remove the card and revert to a plain link. */
  onRemove: () => void;
}

/**
 * Rich preview card for an external link (Card B — side thumbnail). Resolves
 * OpenGraph metadata via the nexus `/api/og` endpoint (server-cached) and
 * renders title / description / site + favicon with a side thumbnail. Falls
 * back to a slim text layout when the URL has no image, and to a plain link
 * when the preview is unavailable (cached failure or fetch error).
 */
export function LinkUnfurlCard({ href, onRemove }: LinkUnfurlCardProps) {
  const { data, loading, error } = useOgPreview(href);

  if (loading) {
    return (
      <div className={styles.card} data-testid="link-unfurl-skeleton" contentEditable={false}>
        <Flex vertical gap="xs" className={styles.body}>
          <Skeleton active title={{ width: '50%' }} paragraph={{ rows: 2 }} />
        </Flex>
      </div>
    );
  }

  // No usable preview → plain link (cached 'failed' or a thrown fetch).
  if (error || !data || data.status === 'failed' || !data.title) {
    return (
      <a className={styles.plainLink} href={href} target="_blank" rel="noopener noreferrer">
        {href}
      </a>
    );
  }

  const { title, description, imageUrl, faviconUrl, siteName } = data;

  return (
    // The remove button is a SIBLING of the anchor (not nested inside it) so
    // clicking it can never trigger the anchor's navigation.
    <div className={styles.wrapper} contentEditable={false}>
      <a
        className={clsx(styles.card, !imageUrl && styles.slim)}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Flex vertical gap="xs" className={styles.body}>
          <Flex align="center" gap="xs" className={styles.siteRow}>
            {faviconUrl ? (
              <Image src={faviconUrl} alt="" preview={false} className={styles.favicon} />
            ) : (
              <Icon name="public" size={iconSize.xs} weight={300} />
            )}
            {siteName && (
              <Text size="xs" className={styles.site}>
                {siteName}
              </Text>
            )}
          </Flex>
          <Text size="sm" className={styles.title}>
            {title}
          </Text>
          {description && (
            <Text size="xs" className={styles.description}>
              {description}
            </Text>
          )}
        </Flex>
        {imageUrl && (
          <div className={styles.thumb}>
            <Image src={imageUrl} alt="" preview={false} className={styles.thumbImg} />
          </div>
        )}
      </a>
      <IconButton
        size="sm"
        transparent
        pill
        aria-label="Remove link preview"
        className={styles.remove}
        onClick={() => onRemove()}
      >
        <Icon name="close" size={iconSize.sm} weight={300} />
      </IconButton>
    </div>
  );
}
